import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { toast } from "react-toastify";
import style from "../common.module.scss";
import { File } from "../../../../services";
import EyeIcon from "../../../shared/icons/EyeIcon";
import Loading from "../../../shared/Loading/Loading";
import { useClickOutside } from "../../../../hooks/useClickOutside";
import DocumentNewIcon from "../../../shared/icons/DocumentNewIcon";
import PhotoViewer from "../../../partners/components/PhotoViewer";
import moment from "moment";
import UnloadIcon from "../../../shared/icons/UnloadIcon";
import { useSelector } from "react-redux";
import { SquarePen } from "lucide-react";
import ConfirmModal from "../../../shared/ConfirmModal/ConfirmModal";
import { useScreenSize } from "../../../../hooks/useScreenSize";

const getDocumentTypeName = (documentType, t) => {
  const typeMap = {
    1: t("orderInfo.listDocsOrder.docTypeCargoPhoto"),
    2: t("orderInfo.listDocsOrder.docTypeSealLoading"),
    3: t("orderInfo.listDocsOrder.docTypeWaybill"),
    4: t("orderInfo.listDocsOrder.docTypeSealUnloading"),
  };
  return typeMap[documentType] || t("orderInfo.listDocsOrder.docTypeDefault");
};

const handleFileOperation = async ({
  doc,
  operation,
  setLoading,
  setCachedFiles,
  cachedFiles,
  setImgUrl,
  setIsOpen,
  setCurrentDocType,
  t,
}) => {
  if (!doc?.fileId) return;

  const docKey = doc.id || doc.fileId;
  setLoading(docKey, true);

  try {
    let cachedData = cachedFiles[doc.fileId];
    let fileURL, contentType;

    if (!cachedData) {
      const response = await File.downloadFileById(doc.fileId);
      contentType = response.headers["content-type"];
      const file = new Blob([response.data], { type: contentType });
      fileURL = URL.createObjectURL(file);

      cachedData = { url: fileURL, contentType };
      setCachedFiles((prev) => ({ ...prev, [doc.fileId]: cachedData }));
    } else {
      fileURL = cachedData.url || cachedData;
      contentType = cachedData.contentType;
    }

    if (operation === "view") {
      const isImage =
        contentType?.startsWith("image/") ||
        doc.fileName?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|svg)$/) ||
        fileURL.includes("image");

      if (isImage) {
        setImgUrl(fileURL);
        setCurrentDocType && setCurrentDocType(doc);
        setIsOpen(true);
      } else {
        window.open(fileURL, "_blank");
      }
    } else if (operation === "download") {
      const a = document.createElement("a");
      a.href = fileURL;
      a.setAttribute("download", doc.fileName || "file");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  } catch (error) {
    console.error(`File ${operation} error:`, error);
    if (operation === "view") {
      toast.error(t("orderInfo.listDocsOrder.fileViewError") || "Ошибка при открытии файла");
    }
  } finally {
    setLoading(docKey, false);
  }
};

const findDocumentByType = (documents, documentType, t) => {
  const doc = documents.find((x) => x.documentType === documentType);
  if (!doc) {
    alert(t("orderInfo.listDocsOrder.photoNotFound"));
    return null;
  }
  return doc;
};

const ListDocsOrder = ({ setShowModal, documents, listFiles = [], orderData }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state);
  const { isMobileView } = useScreenSize();
  const [loadingStates, setLoadingStates] = useState({});
  const [loadingStatesDownload, setLoadingStatesDownload] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [imgUrl, setImgUrl] = useState();
  const [currentDoc, setCurrentDoc] = useState(null);
  const [cachedFiles, setCachedFiles] = useState({});
  const [showConfirmReplace, setShowConfirmReplace] = useState(false);
  const [docToReplace, setDocToReplace] = useState(null);
  const [isReplacing, setIsReplacing] = useState(false);
  const fileInputRef = useRef(null);

  const modalRef = useRef(null);

  useClickOutside(modalRef, () => {
    setShowModal(false);
  });

  const handleClose = () => {
    setIsOpen(false);
  };

  const setLoadingViewer = (docId, isLoading) => {
    setLoadingStates((prev) => ({ ...prev, [docId]: isLoading }));
  };

  const setLoadingDownload = (docId, isLoading) => {
    setLoadingStatesDownload((prev) => ({ ...prev, [docId]: isLoading }));
  };

  const onDownload = (doc) => {
    if (!doc) return;

    handleFileOperation({
      doc,
      operation: "download",
      setLoading: setLoadingDownload,
      setCachedFiles,
      cachedFiles,
      setImgUrl: null,
      setIsOpen: null,
      setCurrentDocType: null,
      t,
    });
  };

  const handleReplaceClick = (doc) => {
    setDocToReplace(doc);
    setShowConfirmReplace(true);
  };

  const handleConfirmReplace = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !docToReplace) return;

    setIsReplacing(true);

    try {
      const formData = new FormData();
      formData.append("type", String(docToReplace.documentType));
      formData.append("orderId", orderData.id);
      formData.append("addressId", docToReplace.addressId || "");
      formData.append("file", file);
      formData.append("FileName", file.name);
      formData.append("name", file.name);
      formData.append("Name", file.name);

      await File.addOrderDoc(formData);

      toast.success(t("orderInfo.listDocsOrder.replaceSuccess"));
      setShowConfirmReplace(false);
      setDocToReplace(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error replacing document:", error);
      toast.error(t("orderInfo.listDocsOrder.replaceError"));
    } finally {
      setIsReplacing(false);
    }
  };

  const handleCancelReplace = () => {
    setShowConfirmReplace(false);
    setDocToReplace(null);
  };

  const DocumentItem = ({ doc, index, isFromListFiles = false }) => (
    <div key={index} className={style.documentItem}>
      <div className={style.documentItem_icon}>
        <DocumentNewIcon />
      </div>
      <div className={style.documentItem_content}>
        <div className={cn(style.documentItem_name, isMobileView ? style.documentItem_name_mobile : style.documentItem_name_desktop)}>
          {isFromListFiles ? doc.fileName : getDocumentTypeName(doc.documentType, t)}
        </div>
        <div className={style.documentItem_size}>
          {moment(doc?.createdAt).format("DD.MM.YYYY")} {t("orderInfo.listDocsOrder.dateTimeConnector")}{" "}
          {moment(doc?.createdAt).format("HH:mm")}
        </div>
      </div>

      {orderData?.status !== "completed" && user && user?.companyId === doc?.companyId && (
        <SquarePen
          color="#6D7885"
          size={22}
          style={{ cursor: "pointer" }}
          onClick={() => handleReplaceClick(doc)}
        />
      )}

      {loadingStatesDownload[doc?.id || doc?.fileId] ? (
        <Loading inBlock marginBottom={0} />
      ) : (
        <UnloadIcon
          onClick={() => {
            handleFileOperation({
              doc,
              operation: "download",
              setLoading: setLoadingDownload,
              setCachedFiles,
              cachedFiles,
              t,
            });
          }}
          style={{ cursor: "pointer" }}
        />
      )}

      {loadingStates[doc?.id || doc?.fileId] ? (
        <Loading inBlock marginBottom={0} />
      ) : (
        <EyeIcon
          onClick={() => {
            handleFileOperation({
              doc,
              operation: "view",
              setLoading: setLoadingViewer,
              setCachedFiles,
              cachedFiles,
              setImgUrl,
              setIsOpen,
              setCurrentDocType: setCurrentDoc,
              t,
            });
          }}
          style={{ cursor: "pointer" }}
        />
      )}
    </div>
  );

  return (
    <>
      <div className={style.cargo_wrapper}>
        {listFiles && listFiles.length > 0 && (
          <div className={style.documentsList}>
            {listFiles.map((doc, index) => (
              <DocumentItem key={`listFile-${index}`} doc={doc} index={index} isFromListFiles={true} />
            ))}
          </div>
        )}

        {documents && documents.length > 0 && (
          <div className={style.documentsList}>
            {documents.map((doc, index) => (
              <DocumentItem key={`document-${index}`} doc={doc} index={index} isFromListFiles={false} />
            ))}
          </div>
        )}
      </div>

      {isOpen && (
        <PhotoViewer
          imgUrl={imgUrl}
          onClose={handleClose}
          onDownload={() => onDownload(currentDoc)}
          ShowBtnDownload
        />
      )}

      <ConfirmModal
        isOpen={showConfirmReplace}
        title={t("orderInfo.listDocsOrder.confirmReplaceTitle")}
        message={t("orderInfo.listDocsOrder.confirmReplaceMessage", {
          docName: getDocumentTypeName(docToReplace?.documentType, t) || "",
        })}
        cancelText={t("common.cancel")}
        confirmText={t("common.confirm")}
        onCancel={handleCancelReplace}
        onConfirm={handleConfirmReplace}
        isLoading={isReplacing}
      />

      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />
    </>
  );
};

export default ListDocsOrder;
